import { JSX, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import Select, { components } from "react-select";
import { fromJS } from "immutable";
import { PlusIcon, X } from "lucide-react";

const MenuPortal = (props) => {
  return <components.MenuPortal {...props} className="react-select-menu-portal" />;
};

const Menu = (props: JSX.IntrinsicElements["div"]) => {
  return (
    <div
      style={{
        backgroundColor: "white",
        borderTopLeftRadius: 4,
        borderTopRightRadius: 4,
        border: "1px solid #e5e7eb",
        marginTop: 8,
        position: "absolute",
        zIndex: 9999,
      }}
      {...props}
    />
  );
};

const Blanket = (props: JSX.IntrinsicElements["div"]) => (
  <div
    style={{
      bottom: 0,
      left: 0,
      top: 0,
      right: 0,
      position: "fixed",
      zIndex: 9998,
    }}
    {...props}
  />
);

const Dropdown = ({
  children,
  isOpen,
  target,
  onClose,
}: {
  children?: React.ReactNode;
  readonly isOpen: boolean;
  readonly target: React.ReactNode;
  readonly onClose: () => void;
}) => (
  <div>
    {target}
    {isOpen ? <Menu>{children}</Menu> : null}
    {isOpen ? <Blanket onClick={onClose} /> : null}
  </div>
);

const ActivityAssignee = ({ title, addButtonText, eventData, handleChange }) => {
  const { t } = useTranslation();

  const members =
    useSelector((state: any) => state.projectReducer.getIn(["project", "members"])) || fromJS([]);

  const memberOptions = members.toJS().map((item) => ({
    value: item.accountId,
    label: item.displayName,
    data: item,
  }));

  const [isOpen, setIsOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  useEffect(() => {
    if (eventData.assignees && eventData.assignees.length > 0) {
      const membersData = members.toJS().map((item) => ({
        value: item.accountId,
        label: item.displayName,
        data: item,
      }));
      const selectedItemsData = membersData.filter((item) =>
        eventData.assignees.includes(item.value)
      );
      setSelectedItems([...selectedItemsData]);
    } else {
      setSelectedItems([]);
    }
  }, [eventData.assignees.length, members]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRemove = (value) => {
    const assignees = selectedItems
      .filter((item) => item.value !== value)
      .map((item) => item.value);
    handleChange("assignees", assignees);
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-between">
        <div>
          <label className="block text-sm font-semibold text-gray-800 mb-2">{title}</label>
        </div>
        <div>
          <Dropdown
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            target={
              <div
                className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded border border-gray-200 shadow-sm cursor-pointer"
                onClick={() => setIsOpen((prev) => !prev)}
              >
                <PlusIcon className="w-3 h-3" />
                <span>{addButtonText}</span>
              </div>
            }
          >
            <Select
              autoFocus
              backspaceRemovesValue={false}
              components={{ DropdownIndicator: null, IndicatorSeparator: null, MenuPortal }}
              controlShouldRenderValue={false}
              // hideSelectedOptions={false}
              tabSelectsValue={false}
              isClearable={false}
              menuIsOpen
              isMulti
              value={selectedItems}
              onChange={(values) => {
                if (values) {
                  const assignees = values.map((value: any) => value.value);
                  handleChange("assignees", assignees);
                } else {
                  handleChange("assignees", []);
                }
                setIsOpen(false);
              }}
              options={memberOptions}
              placeholder={t("Search")}
              formatOptionLabel={(option) => {
                return (
                  <div
                    className={"w-full hover:text-blue-600 flex items-center space-x-2"}
                    key={option.value}
                  >
                    {option.data?.avatarUrl ? (
                      <img
                        src={option.data?.avatarUrl}
                        alt={option.label}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full">
                        <svg
                          className="h-full w-full text-gray-300"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 text-base">{option.label}</div>
                      {option.data?.email && (
                        <p className="text-sm text-gray-600">{option.data?.email}</p>
                      )}
                    </div>
                  </div>
                );
              }}
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  minWidth: 300,
                  minHeight: 38,
                  margin: 8,
                  borderRadius: 8,
                  backgroundColor: state.isDisabled ? "#fbf9fa" : "white",
                  borderColor: state.isFocused ? "#2563eb" : "#e5e7eb",
                  boxShadow: state.isFocused ? "0 0 0 4px rgba(37,99,235,0.1)" : "none",
                  ":hover": {
                    borderColor: state.isFocused ? "#2563eb" : "#d1d5db",
                  },
                  fontSize: 14,
                }),
                menu: (provided) => ({
                  ...provided,
                  borderTopLeftRadius: 0,
                  borderTopRightRadius: 0,
                }),
                placeholder: (provided) => ({
                  ...provided,
                  color: "#6b7280",
                  fontWeight: 500,
                }),
                option: (provided, state) => ({
                  ...provided,
                  fontSize: 14,
                  fontWeight: 500,
                  backgroundColor: state.isSelected
                    ? "#dbeafe"
                    : state.isFocused
                      ? "#f3f4f6"
                      : "white",
                  color: state.isDisabled ? "#6b7280" : "#111827",
                  ":active": {
                    backgroundColor: "#e5e7eb",
                  },
                }),
                menuPortal: (provided) => ({
                  ...provided,
                  zIndex: 9999,
                }),
              }}
            />
          </Dropdown>
        </div>
      </div>
      <div className="p-2 space-y-4">
        {selectedItems.map((item) => {
          return (
            <div key={item.value} className="w-full flex items-center justify-between gap-2">
              <div className="flex items-center gap-x-2">
                {item.data?.avatarUrl ? (
                  <img
                    className="h-6 w-6 flex-none rounded-full bg-gray-50"
                    src={item.data?.avatarUrl}
                    alt={item.label}
                  />
                ) : (
                  <div className="w-6 h-6 rounded-full">
                    <svg
                      className="h-full w-full text-gray-300"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </div>
                )}
                <div
                  title={`${item.label} (${item.data?.email})`}
                  className="text-sm font-semibold leading-6 text-gray-900"
                >
                  {item.label}
                </div>
                <div className="hidden sm:block ml-2 text-sm font-medium leading-6 text-gray-400">
                  {item.data?.email}
                </div>
              </div>
              <div className="flex items-center gap-x-2">
                <X
                  className="w-4 h-4 text-gray-500 cursor-pointer"
                  onClick={() => handleRemove(item.value)}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ActivityAssignee;
